// src/oura/screens/TimelineTab.tsx
//
// The dated schedule the merchant is promised (ourteam-frontend#3714, `T-5`).
//
// The execution timeline used to be rendered by a script on one Mac and sent as
// a PDF. This is the same engine's output (ourteam-backend#4818/#4819), read
// through GET /businesses/{id}/timeline.
//
// CONFLICTS BLOCK PUBLISHING RATHER THAN WARN. The engine used to write them to
// stderr and exit 0, which is precisely how a schedule that contradicted a
// signed contract reached a merchant. They are rendered here with the sentence
// the engine wrote, and the tab says plainly that nothing can be published
// while any of them stand.
//
// Publishing itself is T-6 and lives on the review screen (#3757); this tab
// reads.

import { useEffect, useState } from "react";
import type { ReactNode } from "react";
// CalendarClockIcon is used, twice: the "Nothing has been sold" and "No schedule can be computed" empty states.
import { ArrowRightIcon, CalendarClockIcon, Loader2Icon } from "lucide-react";
import { Surface, TYPE } from "@oura/ui";
import { cn } from "../lib/cn";
import { EmptyState } from "../primitives/EmptyState";
import { shortDate } from "../lib/oura";
import { getTimeline, type TimelinePayload } from "@service/aeo/clientReview";
import { CardHead, TimelineReview } from "../components/TimelineReview";
import { START_PLAN_HINT } from "../delivery/startPlanHint";
import { KEYWORDS_FIELD_ID, SIGNATURE_FIELD_ID } from "../components/plan/planModel";

/**
 * Where a conflict is fixed, when a screen can take someone there (#3975).
 * Only codes with a field to go to are listed: a conflict nobody can act on
 * gets no link rather than a link that goes nowhere.
 */
const CONFLICT_FIX: Record<string, { label: string; field: string }> = {
    priority_keywords_incomplete: { label: "Add priority keywords", field: KEYWORDS_FIELD_ID },
    signature_incomplete: { label: "Record the signature questions", field: SIGNATURE_FIELD_ID },
};

/**
 * What a conflict IS, in plain words. The raw code used to be printed under
 * each one — staff read `signature_incomplete` and `signature.visibility` off
 * the screen (#3989) — and a code is an identifier for a bug report, not a
 * sentence for a reader. A code with no plain name here shows nothing rather
 * than showing itself.
 */
/**
 * The server's sentence, in staff words where it names a field: "priority_keywords
 * has 2 entries; the catalogue captures 5 to 10..." reads as how many are recorded.
 */
function plainConflict(c: { code: string; message: string }): string {
    if (c.code === "priority_keywords_incomplete") {
        const n = /has (\d+) entr/.exec(c.message)?.[1];
        if (n) return `${n} of the 5 to 10 priority keywords agreed at the quotation ${n === "1" ? "is" : "are"} recorded. Add the rest before publishing.`;
    }
    return c.message;
}

const CONFLICT_TITLE: Record<string, string> = {
    priority_keywords_incomplete: "Priority keywords",
    signature_incomplete: "The signature",
};

/** One dated fact: a sentence-case label above its value. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="min-w-0">
            <dt className={TYPE.lab}>{label}</dt>
            <dd className={cn(TYPE.tb, "m-0 mt-0.5 font-semibold text-gray-900")}>{children}</dd>
        </div>
    );
}

export function TimelineTab({ businessId, onFixConflict }: { businessId: string; onFixConflict?: (field: string) => void }) {
    const [payload, setPayload] = useState<TimelinePayload | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        void getTimeline(businessId)
            .then((p) => { if (!cancelled) setPayload(p); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [businessId]);

    if (loading) {
        return (
            <div className="flex justify-center py-24">
                <Loader2Icon className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        );
    }

    // No engagement at all: the endpoint 404s, which is the same state the
    // Contract tab names. Say the same thing, in the same words.
    if (!payload) {
        return (
            <EmptyState
                icon={CalendarClockIcon}
                title="Nothing has been sold to this business yet"
                description={`A schedule appears here once a signed quotation is activated. ${START_PLAN_HINT}`}
            />
        );
    }

    const { computed, published, conflicts, problems, changePending } = payload;

    const reload = () => {
        setLoading(true);
        void getTimeline(businessId)
            .then((p) => setPayload(p))
            .finally(() => setLoading(false));
    };

    return (
        <div className="space-y-4">
            {/* An unpublished edit is the state this whole track exists to make
                visible: the plan on screen is not the plan the merchant was told. */}
            {changePending && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <span className="font-medium">This plan has an unpublished change.</span>{" "}
                    {published ? `The client is still looking at version ${published.version}.` : "The client has no schedule yet."}
                </div>
            )}

            {/* What blocks publishing comes first, and is listed ONCE. It used to
                appear both inside the publish card and again in a card of its
                own, so the same two sentences were read twice on one screen. */}
            {conflicts.length > 0 && (
                <Surface>
                    <CardHead
                        label="Before publishing"
                        title={`${conflicts.length} ${conflicts.length === 1 ? "thing" : "things"} to fix before publishing`}
                        sub="A published schedule is what the client is told. Each of these is the contract and the catalogue disagreeing about something signed."
                    />
                    <div className="mt-4">
                        <ul className="list-plain space-y-2 p-0">
                            {conflicts.map((c) => {
                                const fix = onFixConflict ? CONFLICT_FIX[c.code] : undefined;
                                return (
                                    <li key={c.code} className="flex gap-3 rounded-lg bg-rose-50 px-3 py-2.5">
                                        <span aria-hidden className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-rose-400" />
                                        <div className="min-w-0">
                                            <p className="text-sm leading-relaxed text-rose-900">{plainConflict(c)}</p>
                                            {/* The field that clears it, not only the code it is called by. */}
                                            {fix && (
                                                <button
                                                    type="button"
                                                    onClick={() => onFixConflict?.(fix.field)}
                                                    className="mt-1 inline-flex items-center gap-1 rounded text-sm font-medium text-rose-800 underline decoration-rose-300 underline-offset-2 hover:decoration-rose-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                                                >
                                                    {fix.label} <ArrowRightIcon aria-hidden className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                            {CONFLICT_TITLE[c.code] && (
                                                <p className={cn(TYPE.tm, "m-0 mt-0.5 font-medium text-rose-500")}>{CONFLICT_TITLE[c.code]}</p>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </Surface>
            )}

            {/* The date diff and the publish button (#3757). Above the schedule,
                because a reviewer opens this tab to decide whether to publish —
                the term dates below are what they check that decision against. */}
            <TimelineReview businessId={businessId} payload={payload} onPublished={reload} />

            {/* A schedule that cannot be computed at all says why, rather than
                rendering an empty term. */}
            {!computed && (
                <EmptyState
                    icon={CalendarClockIcon}
                    title="No schedule can be computed yet"
                    description={problems[0] ?? "The engine could not build a schedule from this contract."}
                />
            )}

            {computed && (
                <>
                    <Surface>
                        {/* The published state is said once, on the publish card
                            above; this card is the dates. */}
                        <CardHead label="Dates" title="The term" />
                        <div className="mt-4">
                            {/* `*_text` is the engine's own formatting; the ISO field
                                is what a screen should format itself, and shortDate
                                wants a date-only string, which these are. */}
                            <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                                <Fact label="Work starts"><span className="tabular-nums">{shortDate(computed.clock_start)}</span></Fact>
                                <Fact label="Keywords needed by"><span className="tabular-nums">{shortDate(computed.keywords_by)}</span></Fact>
                                <Fact label="Handover"><span className="tabular-nums">{shortDate(computed.handover)}</span></Fact>
                                <Fact label="Term ends"><span className="tabular-nums">{shortDate(computed.term_end)}</span></Fact>
                            </dl>
                            {(computed.term_months_source !== "contract" || computed.total_units_source !== "contract") && (
                                <p className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-500">
                                    {/* The engine says which source it used, and the
                                        catalogue being used means the CONTRACT was
                                        silent — worth knowing before publishing. */}
                                    Read from the catalogue, not the contract:{" "}
                                    {[
                                        computed.term_months_source !== "contract" ? "term length" : null,
                                        computed.total_units_source !== "contract" ? "page total" : null,
                                    ].filter(Boolean).join(" and ")}.
                                </p>
                            )}
                        </div>
                    </Surface>

                    <Surface>
                        <CardHead label="Schedule" title="Month by month" sub={`${computed.months.length} months`} />
                        <div className="mt-4">
                            {/* Two columns: WHEN on the left, WHAT on the right. A
                                month read as one line of heading, window and report
                                date run together, with its deliverables underneath
                                in the same weight, so a month's edge was hard to find. */}
                            <ol className="list-plain divide-y divide-gray-100 p-0">
                                {computed.months.map((m) => (
                                    <li key={m.term_month} className="grid gap-2 py-3 first:pt-0 last:pb-0 sm:grid-cols-[13rem_1fr] sm:gap-6">
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900">Month {m.term_month}</p>
                                            <p className="mt-0.5 text-xs text-gray-500">{m.window}</p>
                                            <p className="mt-0.5 text-xs text-gray-400 tabular-nums">Report {shortDate(m.report_date)}</p>
                                        </div>
                                        {m.lands.length > 0 ? (
                                            <ul className="list-plain space-y-1.5 p-0">
                                                {m.lands.map((l, i) => (
                                                    <li key={i} className="flex gap-2.5 text-sm text-gray-700">
                                                        <span aria-hidden className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-300" />
                                                        <span>{l}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-sm text-gray-400">Nothing lands this month.</p>
                                        )}
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </Surface>
                </>
            )}
        </div>
    );
}

